import { NetworkStatus, useMutation, useQuery } from "@apollo/client";
import { useMemo } from "react";

import FlashContext from "components/FlashMessages/FlashContext";
import { showMFAConfirmationModal } from "components/MFA/ConfirmationModal";
import SecurityKeysList from "components/MFA/SecurityKeysList";
import { SECURITY_KEY_DELETE } from "components/MFA/gql";
import SearchInput from "components/SearchInput";
import MfaTierInfo from "components/TierInfo/MfaTierInfo";
import NotFoundPage from "components/error/NotFoundPage";
import { EmptystateLockColored } from "components/icons/generated";
import PageLoading from "components/loading/PageLoading";
import { SEARCH_PLACEHOLDERS } from "constants/searchPlaceholders";
import { URL_SEARCH_KEY } from "constants/urlQueryKeys";
import Box from "ds/components/Box";
import Callout from "ds/components/Callout";
import EmptyState from "ds/components/EmptyState";
import FormToggleField from "ds/components/Form/ToggleField";
import Link from "ds/components/Link";
import PageInfoHeader from "ds/components/PageInfoHeader";
import TooltipInfo from "ds/components/TooltipInfo";
import TooltipModalBody from "ds/components/TooltipModal/Body";
import { TooltipModalTitle } from "ds/components/TooltipModal/Title";
import Typography from "ds/components/Typography";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import useURLParams from "hooks/useURLParams";
import { trackSegmentEvent } from "shared/Analytics";
import { BillingTierFeature, SecurityKey } from "types/generated";
import { fuzzySearch } from "utils/fuzzySearch";
import MFASettingsSSOCallout from "views/Account/MFASettingsSSOCallout";
import useTierFeature from "views/Account/hooks/useTierFeature";
import { AccountContext } from "views/AccountWrapper";

import { ENABLE_MFA_CALLOUT, FILTERS_ORDER_SETTINGS_KEY } from "./constants";
import { ACCOUNT_MFA_SETTINGS, ACCOUNT_TOGGLE_ENFORCING_MFA } from "./gql";
import { mapSecurityKeysToView } from "./helpers";
import styles from "./styles.module.css";

type AccountMFASettingsGql = {
  hasSSO: boolean;
  enforceMFA: boolean;
  securityKeys: Array<SecurityKey>;
  viewer: {
    securityKeys: Array<SecurityKey>;
  };
};

const ManageMFA = () => {
  const { accountName } = useTypedContext(AccountContext);
  useTitle(`Organization Settings · Manage multi-factor authentication · ${accountName}`);

  const billingTierHasMfa = useTierFeature(BillingTierFeature.Mfa);

  const { onError, reportSuccess } = useTypedContext(FlashContext);

  const urlParams = useURLParams();
  const searchQuery = urlParams.get(URL_SEARCH_KEY);

  const { loading, data, error, networkStatus } =
    useQuery<AccountMFASettingsGql>(ACCOUNT_MFA_SETTINGS);

  const [deleteSecurityKey] = useMutation(SECURITY_KEY_DELETE, {
    refetchQueries: ["AccountMFASettings"],
  });

  const [toggleEnforcingMfa, { loading: isEnforcing }] = useMutation(ACCOUNT_TOGGLE_ENFORCING_MFA, {
    refetchQueries: ["AccountMFASettings"],
    awaitRefetchQueries: true,
  });

  const handleDeleteKey = (key: SecurityKey) => {
    deleteSecurityKey({ variables: { id: key.id } })
      .then(() => {
        reportSuccess({ message: "Security key was successfully deleted" });
      })
      .catch(onError);
  };

  const handleToggleEnforce = () => {
    toggleEnforcingMfa()
      .then(({ data }) => {
        const isEnforced = data.accountToggleEnforcingMFA;

        reportSuccess({
          message: isEnforced
            ? "MFA was enforced successfully!"
            : "MFA enforcement was disabled successfully!",
        });

        if (isEnforced) {
          trackSegmentEvent("MFA Enforced");
        }
      })
      .catch(onError);
  };

  const handleDeleteKeyConfirmation = (key: SecurityKey) =>
    showMFAConfirmationModal({
      title: "Delete security key",
      mainActionText: "Delete",
      onConfirm: () => handleDeleteKey(key),
      content: (
        <>
          Are you sure you want to delete <strong>{key.name}</strong>?
        </>
      ),
    });

  const handleEnforceConfirmation = () => {
    const title = isEnforcedMFA
      ? "Disable multi-factor enforcement"
      : "Enforce multi-factor authentication";

    const content = isEnforcedMFA ? (
      <>
        If you disable the feature all users <strong>will not be forced</strong> to use additional
        authentication to login to your organization.
        <br />
        <br />
        Are you sure you would like to disable?
      </>
    ) : (
      <>
        After enabling multi-factor authentication all active users will be asked to{" "}
        <strong>register their security key</strong> during next login and active sessions (except
        the current one) will be invalidated.
      </>
    );

    const mainActionText = isEnforcedMFA ? "Disable" : "Enforce";
    const mainActionVariant = isEnforcedMFA ? "dangerPrimary" : "primary";

    showMFAConfirmationModal({
      title,
      mainActionText,
      mainActionVariant,
      onConfirm: handleToggleEnforce,
      content,
    });
  };

  const ErrorContent = useErrorHandle(error);

  const securityKeys = data?.securityKeys;
  const viewerSecurityKeys = data?.viewer?.securityKeys;
  const isEnforcedMFA = !!data?.enforceMFA;

  const filteredSecurityKeys = useMemo(() => {
    const filteredSecurityKeys = securityKeys || [];

    if (searchQuery) {
      return fuzzySearch(filteredSecurityKeys, searchQuery.trim(), {
        keys: ["owner", "name", "id"],
        scoreThreshold: -1000,
      });
    }

    return filteredSecurityKeys;
  }, [securityKeys, searchQuery]);

  const securityKeyViews = useMemo(
    () => mapSecurityKeysToView(filteredSecurityKeys, viewerSecurityKeys, isEnforcedMFA),
    [isEnforcedMFA, filteredSecurityKeys, viewerSecurityKeys]
  );

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !securityKeys && networkStatus === NetworkStatus.loading) {
    return <PageLoading />;
  }

  if (!securityKeys) {
    return <NotFoundPage />;
  }

  const hasSsoWarning = !data.hasSSO;
  const hasKeys = securityKeys.length > 0;
  const viewerHasKeys = data.viewer?.securityKeys?.length > 0;
  const isEnforceToggleDisabled =
    isEnforcing || (!isEnforcedMFA && (!viewerHasKeys || hasSsoWarning || !billingTierHasMfa));

  return (
    <>
      <PageInfoHeader title="Multi-factor authentication" />

      <Box className={styles.wrapper} direction="column" grow="1" fullWidth>
        {billingTierHasMfa ? (
          <>
            {!hasSsoWarning && !isEnforcedMFA && !viewerHasKeys && (
              <Callout
                title="Enforce on organization level"
                variant="info"
                storageKey={ENABLE_MFA_CALLOUT}
              >
                Enable MFA by adding your personal security key in{" "}
                <Link href="/personal-settings/mfa" size="small">
                  Personal settings
                </Link>{" "}
                before enforcing on organization level
              </Callout>
            )}
            {hasSsoWarning && <MFASettingsSSOCallout isAdmin />}
          </>
        ) : (
          <MfaTierInfo hasKeys={hasKeys} />
        )}

        <Box className={styles.enforceBox}>
          <FormToggleField
            variant="switch"
            onChange={handleEnforceConfirmation}
            checked={isEnforcedMFA}
            disabled={isEnforceToggleDisabled}
            title="Enforce multi-factor authentication"
            description="As an organization owner, you can require that all members of your organization’s teams enable two-factor authentication. If you do, next time they login, they will need to register their key."
          />
        </Box>

        {hasKeys && (
          <>
            <Box className={styles.securityKeysBox} direction="row" align="center" gap="large">
              <Box direction="row" grow="1" align="center" gap="medium">
                <Typography tag="p" variant="p-t6">
                  Security keys
                </Typography>
                <TooltipInfo variant="modal">
                  <TooltipModalTitle>Security keys</TooltipModalTitle>
                  <TooltipModalBody>
                    Security keys are hardware devices that can be used as your second factor of
                    authentication.
                    <br />
                    <br />
                    Manage your organization keys from the list below.
                  </TooltipModalBody>
                </TooltipInfo>
              </Box>
              <SearchInput
                placeholder={SEARCH_PLACEHOLDERS.MFA_KEYS}
                filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY}
              />
            </Box>
            <SecurityKeysList
              securityKeys={securityKeyViews}
              onDelete={handleDeleteKeyConfirmation}
              showOwner
            />
          </>
        )}

        {isEnforcedMFA && !viewerHasKeys && (
          <EmptyState title="No security keys added yet" icon={EmptystateLockColored} />
        )}
      </Box>
    </>
  );
};

export default ManageMFA;
